import { useParams } from "react-router-dom";

import { View } from "./View";
import {
  IPageAndSearchRequest,
  IQueryStringNavLinks,
  ITableHeader,
  IViewBatchTransfer,
  IViewSingleTransfer,
  IViewTeamMember,
  PagedMeta,
} from "../../../../interfaces-and-types";
import {
  useApiPagedMeta,
  useGetPageQueryParams,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useQueryString,
  useQueryStringFilter,
  useReactRouterLink,
} from "../../../../hooks";
import { filterConstants } from "../../../../constants";
import { useEffect } from "react";
import { ITeamAndSecurityQueryMeta } from "../../../TeamAndSecurity/TeamAndSecurity";

interface ISingleTransfersQueryMeta {
  status: string | null;
  batch_ref: string;
}

export interface ITransferBatchDetailsModalViewProps {
  transfersInBatch: IViewSingleTransfer[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  isModalOpen: boolean;
  closeModal: () => void;
  openSingleTransferDetailsReceiptModal: (data: any) => void;

  // extra buttons
  onExport: () => void;

  isLoadingBatch: boolean;
  isErrorBatch: boolean;
  batch: IViewBatchTransfer | null;

  retryFailedTransfersInBatch: () => void;
  isLoadingAuthorizeTransfersInBatch: boolean;
  isSuccessAuthorizeTransfersInBatch: boolean;
  authorizeTransfersInBatch: () => void;

  isLoadingTeamMembers: boolean;
  isSuccessTeamMembers: boolean;
  teamMembersList: IViewTeamMember[];
}

export const TRANSFERS_LIST_TABLE_NAME = "trsfs";

const appendSingleTransfersQueryStringPrefix = (queryKey: string) =>
  `${TRANSFERS_LIST_TABLE_NAME}.${queryKey}`;

export const TransferBatchDetailsModal = () => {
  const query = useQueryString();
  const status = query.get(
    `${TRANSFERS_LIST_TABLE_NAME}.${filterConstants.statusKey}`
  );
  const { page, page_size, search } = useGetPageQueryParams({
    queryStringPrefix: TRANSFERS_LIST_TABLE_NAME,
  });

  const { setApiPageMeta } = useApiPagedMeta();

  const { setQueryStringFilter, clearAllQueryStringsWithPrefix } =
    useQueryStringFilter([]);

  const { location, navigateToRoutePreserveParams } = useReactRouterLink();

  const headers: ITableHeader[] = [
    {
      key: "amount",
      header: "Amount",
    },
    {
      key: "transfer_fee",
      header: "Fees",
    },
    {
      key: "rate",
      header: "Rate",
    },
    {
      key: "bank_name",
      header: (
        <>
          Bank Name
          {/* <br /> /
          Gateway Name */}
        </>
      ),
    },
    {
      key: "account_number",
      header: (
        <>
          Account No
          {/* <br /> /
          Merchant Code */}
        </>
      ),
    },
    {
      key: "account_name",
      header: (
        <>
          Account Name
          {/* <br /> /
          Merchant Name */}
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
    },
    {
      key: "session_id",
      header: "Session Id",
    },
    {
      key: "created_at",
      header: "Created On",
    },
    {
      key: "paid_at",
      header: "Paid On",
    },
    {
      key: "viewReceipt",
      header: "",
    },
    // {
    //   key: "more",
    //   header: "",
    // },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "All",
        value: null,
      },
      {
        key: "Successful",
        value: "successful",
      },
      {
        key: "Processing",
        value: "processing",
      },
      {
        key: "Failed",
        value: "failed",
      },
    ],
  };

  const { batch_ref } = useParams();

  const {
    isLoading: isLoadingBatch,
    isSuccess: isSuccessBatch,
    isError: isErrorBatch,
    data: batch,
  } = useMerchantApiQueryWithParams<{ batch_ref: string }, IViewBatchTransfer>(
    [
      "getBatchByRef",
      {
        batch_ref: batch_ref!, // NOTE: used to enforce react query cache with correct queryKey
      },
    ],
    {
      enabled: !!batch_ref,
    },
    (urlTemplate) => urlTemplate.replace("batch_ref", batch_ref!)
  );

  const {
    isLoading,
    // isSuccess,
    isError,
    data: pagedSingleTransfersListByRef,
    meta: pagedSingleTransfersMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & ISingleTransfersQueryMeta,
    IViewSingleTransfer[],
    PagedMeta
  >(
    [
      "getSingleTransfersListByBatchRef",
      {
        page: (page && Number(page)) || 1,
        page_size: (page_size && Number(page_size)) || 10,
        search,
        status,
        batch_ref: batch_ref!, // NOTE: used to enforce react query cache with correct queryKey
      },
    ],
    {
      enabled: !!batch_ref,
    },
    (urlTemplate) => urlTemplate.replace("batch_ref", batch_ref!)
  );

  // setApiPageMeta({
  //   // page: pagedSingleTransfersMeta?.page_meta.page,
  //   // page_size: pagedSingleTransfersMeta?.page_meta.page_size,
  //   page_count: pagedSingleTransfersMeta?.page_meta.page_count || 1,
  //   item_count: pagedSingleTransfersMeta?.page_meta.item_count || 0,
  //   // search: pagedSingleTransfersMeta?.page_meta.search,
  // })

  const {
    // isSuccess: isSuccessRetryFailedTransfersInBatch,
    mutate: requeueFailedInBatchByRef,
  } = useMerchantApiMutation<null, IViewBatchTransfer>(
    "requeueFailedInBatchByRef",
    [],
    {},
    (urlTemplate) => urlTemplate.replace("batch_ref", batch_ref!)
  ).usePostMutation;

  const retryFailedTransfersInBatch = () => requeueFailedInBatchByRef(null);

  const onExport = () => {
    // export data via api and download
    alert("exporting data...");
  };

  // BUG: because setQueryStringFilter() is used, the url is captured by the closure
  //  and refuses closing the modal by navigating back to it
  // useEffect(() => {
  //   return () => {
  //     setQueryStringFilter(
  //       appendSingleTransfersQueryStringPrefix(filterConstants.statusKey),
  //       ""
  //     );
  //     setQueryStringFilter(
  //       appendSingleTransfersQueryStringPrefix(filterConstants.searchKey),
  //       ""
  //     );
  //     setQueryStringFilter(
  //       appendSingleTransfersQueryStringPrefix(filterConstants.currentPageKey),
  //       ""
  //     );
  //     setQueryStringFilter(
  //       appendSingleTransfersQueryStringPrefix(filterConstants.pageSizeKey),
  //       ""
  //     );
  //   };
  // }, [location.pathname]);

  // current location: transfers/batch/:batch_ref/transfers
  const closeBatchTransferDetailsModal = () => {
    // BUG: because setQueryStringFilter() is used, the url is captured by the closure
    //  and refuses closing the modal by navigating back to it
    // NOTE: both methods below do not clear query params for modal table
    // // clear all query strings starting with the prefix
    // method 1
    // clearAllQueryStringsWithPrefix(TRANSFERS_LIST_TABLE_NAME);

    // BUG: because setQueryStringFilter() is used, the url is captured by the closure
    //  and refuses closing the modal by navigating back to it
    // method 2
    // setQueryStringFilter(
    //   appendSingleTransfersQueryStringPrefix(filterConstants.searchKey),
    //   ""
    // );
    // setQueryStringFilter(
    //   appendSingleTransfersQueryStringPrefix(filterConstants.currentPageKey),
    //   ""
    // );
    // setQueryStringFilter(
    //   appendSingleTransfersQueryStringPrefix(filterConstants.pageSizeKey),
    //   ""
    // );

    // navigateToRoutePreserveParams(
    //   location.pathname.replace(`/batch/${batch_ref}/transfers`, ""),
    //   true,
    //   `
    //   ${appendSingleTransfersQueryStringPrefix(filterConstants.statusKey)}=
    //   & ${appendSingleTransfersQueryStringPrefix(filterConstants.searchKey)}=
    //   & ${appendSingleTransfersQueryStringPrefix(filterConstants.currentPageKey)}=
    //   & ${appendSingleTransfersQueryStringPrefix(filterConstants.pageSizeKey)}=
    //   `
    // );

    navigateToRoutePreserveParams(
      location.pathname.replace(`/batch/${batch_ref}/transfers`, "")
    );
  };

  const {
    isLoading: isLoadingTeamMembers,
    isError: isErrorTeamMembers,
    isSuccess: isSuccessTeamMembers,
    data: teamMembersList,
    meta: teamMembersMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & ITeamAndSecurityQueryMeta,
    IViewTeamMember[],
    PagedMeta
  >(
    [
      "getTeamMembers",
      {
        page: (page && Number(page)) || 1,
        page_size: (page_size && Number(page_size)) || 100,
        search,
        status: "active",
      },
    ],
    {
      enabled: isSuccessBatch && !!batch && batch.approval_count > 0,
    },
    undefined,
    {
      hideApiErrorMessge: true,
      hideAxiosErrorMessages: true,
      hideResponseMessage: true,
    }
  );

  const {
    isLoading: isLoadingAuthorizeTransfersInBatch,
    isSuccess: isSuccessAuthorizeTransfersInBatch,
    mutate: authorizeTransfersInBatch,
  } = useMerchantApiMutation<{}, IViewBatchTransfer>(
    "approveBatchTransferByRef",
    ["getBatchByRef", "getBatchList"],
    {},
    (urlTemplate) => urlTemplate.replace("batch_ref", batch_ref!)
  ).usePostMutation;

  // view single transfer details
  const openSingleTransferDetailsModal = (transfer_ref: any) =>
    navigateToRoutePreserveParams(`${transfer_ref}/receipt`);

  return (
    <View
      isModalOpen={!!batch_ref}
      closeModal={closeBatchTransferDetailsModal}
      transfersInBatch={pagedSingleTransfersListByRef || []}
      headers={headers}
      isLoading={isLoading}
      isError={isError}
      totalCount={pagedSingleTransfersMeta?.page_meta.item_count || 0}
      queryStringTabs={queryStringTabs}
      onExport={onExport}
      openSingleTransferDetailsReceiptModal={openSingleTransferDetailsModal}
      isLoadingBatch={isLoadingBatch}
      isErrorBatch={isErrorBatch}
      batch={batch || null}
      retryFailedTransfersInBatch={retryFailedTransfersInBatch}
      isLoadingAuthorizeTransfersInBatch={isLoadingAuthorizeTransfersInBatch}
      isSuccessAuthorizeTransfersInBatch={isSuccessAuthorizeTransfersInBatch}
      authorizeTransfersInBatch={() => authorizeTransfersInBatch({})}
      isLoadingTeamMembers={isLoadingTeamMembers}
      isSuccessTeamMembers={isSuccessTeamMembers}
      teamMembersList={teamMembersList || []}
    />
  );
};
